import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Package, PackagePlus, CreditCard,
  Building2, GitBranch, Users, Key, Webhook, LogOut,
  Truck, MapPin, Settings, BarChart2, UserCircle,
  Shield, Activity, DollarSign, AlertTriangle, Printer, Tag,
} from 'lucide-react';
import '../styles/sidebar.css';

interface NavItem { to: string; label: string; icon: React.ReactNode; }
interface NavGroup { title?: string; items: NavItem[]; }

const profileGroup = (base: string): NavGroup => ({
  title: 'Account',
  items: [{ to: `${base}/profile`, label: 'My Profile', icon: <UserCircle size={18} /> }],
});

const NAV_BY_ROLE: Record<string, NavGroup[]> = {
    SUPER_ADMIN: [
     { items: [{ to: '/super-admin/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'Operations', items: [
       { to: '/super-admin/shipments', label: 'All Shipments', icon: <Package size={18} /> },
       { to: '/super-admin/shipments/create', label: 'New Shipment', icon: <PackagePlus size={18} /> },
       { to: '/super-admin/payments', label: 'All Payments', icon: <CreditCard size={18} /> },
       { to: '/super-admin/tracking', label: 'Tracking', icon: <MapPin size={18} /> },
       { to: '/super-admin/transporters', label: 'Transporters', icon: <Truck size={18} /> },
     ]},
     { title: 'Management', items: [
       { to: '/super-admin/companies', label: 'Companies', icon: <Building2 size={18} /> },
       { to: '/super-admin/users', label: 'All Users', icon: <Users size={18} /> },
     ]},
     { title: 'Analytics', items: [
       { to: '/super-admin/reports', label: 'Reports', icon: <BarChart2 size={18} /> },
       { to: '/super-admin/audit-log', label: 'Audit Log', icon: <Shield size={18} /> },
     ]},
     { title: 'Integrations', items: [
       { to: '/super-admin/api-keys', label: 'API Keys', icon: <Key size={18} /> },
       { to: '/super-admin/webhooks', label: 'Webhooks', icon: <Webhook size={18} /> },
     ]},
     { title: 'Administration', items: [
       { to: '/super-admin/billing', label: 'Billing & Plans', icon: <DollarSign size={18} /> },
       { to: '/super-admin/system-health', label: 'System Health', icon: <Activity size={18} /> },
       { to: '/super-admin/settings', label: 'Platform Settings', icon: <Settings size={18} /> },
     ]},
     profileGroup('/super-admin'),
   ],
    COMPANY_ADMIN: [
     { items: [{ to: '/company-admin/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'Deliveries', items: [
       { to: '/company-admin/shipments', label: 'My Orders', icon: <Package size={18} /> },
       { to: '/company-admin/shipments/create', label: 'New Delivery Order', icon: <PackagePlus size={18} /> },
       { to: '/company-admin/payments', label: 'Payments', icon: <CreditCard size={18} /> },
       { to: '/company-admin/tracking', label: 'Track Shipment', icon: <MapPin size={18} /> },
     ]},
     { title: 'Management', items: [
       { to: '/company-admin/users', label: 'Staff', icon: <Users size={18} /> },
       { to: '/company-admin/branches', label: 'Branches', icon: <GitBranch size={18} /> },
     ]},
     { title: 'Analytics', items: [
       { to: '/company-admin/reports', label: 'Reports', icon: <BarChart2 size={18} /> },
       { to: '/company-admin/audit-log', label: 'Audit Log', icon: <Shield size={18} /> },
     ]},
     { title: 'Integrations', items: [
       { to: '/company-admin/api-keys', label: 'API Keys', icon: <Key size={18} /> },
       { to: '/company-admin/webhooks', label: 'Webhooks', icon: <Webhook size={18} /> },
     ]},
     { title: 'Settings', items: [
       { to: '/company-admin/company-profile', label: 'Company Profile', icon: <Settings size={18} /> },
     ]},
     profileGroup('/company-admin'),
   ],
    DISPATCH_AGENT: [
     { items: [{ to: '/dispatch/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'Operations', items: [
       { to: '/dispatch/shipments', label: 'All Shipments', icon: <Package size={18} /> },
       { to: '/dispatch/payments', label: 'Payments', icon: <CreditCard size={18} /> },
       { to: '/dispatch/tracking', label: 'Tracking', icon: <MapPin size={18} /> },
       { to: '/dispatch/transporters', label: 'Transporters', icon: <Truck size={18} /> },
     ]},
     { title: 'Tools', items: [
       { to: '/dispatch/manifest', label: 'Trip Manifest', icon: <Printer size={18} /> },
       { to: '/dispatch/exceptions', label: 'Exceptions', icon: <AlertTriangle size={18} /> },
       { to: '/dispatch/reports', label: 'Reports', icon: <BarChart2 size={18} /> },
     ]},
     profileGroup('/dispatch'),
   ],
    RECOVERY_OFFICER: [
     { items: [{ to: '/recovery/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'Operations', items: [
       { to: '/recovery/shipments', label: 'Shipments', icon: <Package size={18} /> },
       { to: '/recovery/payments', label: 'Payments', icon: <CreditCard size={18} /> },
       { to: '/recovery/tracking', label: 'Tracking', icon: <MapPin size={18} /> },
     ]},
     { title: 'Analytics', items: [
       { to: '/recovery/reports', label: 'Reports', icon: <BarChart2 size={18} /> },
     ]},
     profileGroup('/recovery'),
   ],
    COMPANY_AGENT: [
     { items: [{ to: '/agent/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'Operations', items: [
       { to: '/agent/shipments', label: 'Shipments', icon: <Package size={18} /> },
       { to: '/agent/shipments/create', label: 'New Shipment', icon: <PackagePlus size={18} /> },
       { to: '/agent/payments', label: 'Payments', icon: <CreditCard size={18} /> },
       { to: '/agent/tracking', label: 'Tracking', icon: <MapPin size={18} /> },
     ]},
     { title: 'Analytics', items: [
       { to: '/agent/reports', label: 'Reports', icon: <BarChart2 size={18} /> },
     ]},
     profileGroup('/agent'),
   ],
    INDEPENDENT_USER: [
     { items: [{ to: '/user/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'My Account', items: [
       { to: '/user/shipments', label: 'My Shipments', icon: <Package size={18} /> },
       { to: '/user/shipments/create', label: 'Send Package', icon: <PackagePlus size={18} /> },
       { to: '/user/payments', label: 'My Payments', icon: <CreditCard size={18} /> },
       { to: '/user/tracking', label: 'Track Shipment', icon: <MapPin size={18} /> },
     ]},
     profileGroup('/user'),
   ],
    TRANSPORTER: [
     { items: [{ to: '/transporter/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }] },
     { title: 'My Work', items: [
       { to: '/transporter/shipments', label: 'My Shipments', icon: <Package size={18} /> },
       { to: '/transporter/tracking', label: 'Tracking', icon: <MapPin size={18} /> },
     ]},
     profileGroup('/transporter'),
   ],

};

export const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  if (!user) return null;

  const groups = NAV_BY_ROLE[user.role] ?? [];

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img src="/logo.png" alt="E.H.C Logistics" style={{ height: 36, objectFit: 'contain' }} />
      </div>

      <div className="sidebar-user">
        <div className="sidebar-user-name">{user.firstName} {user.lastName}</div>
        <div className="sidebar-user-role">{user.role.replaceAll('_', ' ')}</div>
      </div>

      <nav className="sidebar-nav">
        {groups.map((group, gi) => (
          <div key={gi}>
            {group.title && (
              <div style={{ padding: '10px 16px 4px', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {group.title}
              </div>
            )}
            {group.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <button className="sidebar-logout" onClick={logout}>
        <LogOut size={18} />
        <span>Logout</span>
      </button>
      <a href="/pricing" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: 13, color: '#64748b', borderTop: '1px solid #e2e8f0', textDecoration: 'none' }}>
        <Tag size={16} />
        <span>Rate Card</span>
      </a>
      <a href="/track" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: 13, color: '#64748b', borderTop: '1px solid #e2e8f0', textDecoration: 'none' }}>
        <MapPin size={16} />
        <span>Public Tracking</span>
      </a>
    </aside>
  );
};
